/* eslint-disable no-console */
import { Subscription } from 'rxjs';
import { filter, take } from 'rxjs/operators';
import { GatewayConnection, GatewayRequest } from '../data/gateway-connection';
import { Http } from '../data/http';
import { Net } from '../data/net';
import { Strings } from '../generated/strings';
import { RpcLogClient } from '../rpc/log/rpc-log-client';
import { RpcLogRecord } from '../rpc/log/rpc-log-model';
import { RpcLogSubjectServer } from '../rpc/log/rpc-log-subject-server';
import { Rpc } from '../rpc/rpc';
import { RpcTelemetryClient } from '../rpc/telemetry/rpc-telemetry-client';
import { RpcTelemetryRecord } from '../rpc/telemetry/rpc-telemetry-model';
import { RpcTelemetrySubjectServer } from '../rpc/telemetry/rpc-telemetry-subject-server';
import { LogLevel } from './log-level';
import { LogRecord } from './log-record';
import { LoggingConstants } from './logging-constants';
import { TelemetryControlType } from './telemetry-control-type';
import { TelemetryRecord } from './telemetry-record';

/**
 * Logger set for log and telemetry.
 */
interface LoggerSet<T> {
    /**
     * Max record to send to gateway. If it reaches then sends to gateway.
     */
    maxRecordLength: number;

    /**
     * Max wait time milliseconds. If it reaches then sends to gateway.
     */
    maxWaitTimeMs: number;

    /**
     * The api url.
     */
    api: string;

    /**
     * Subscription to rpc channel observable.
     */
    rpcSubscription?: Subscription;

    /**
     * Buffer.
     */
    buffer?: T[];

    /**
     * Timer object.
     */
    timer?: any;
}

/**
 * Logging class.
 * @dynamic
 */
export class Logging {
    private static logMaxRecordLength = 20;
    private static logMaxWaitTimeMs = 30000;
    private static telemetryMaxRecordLength = 100;
    private static telemetryMaxWaitTimeMs = 60000;
    private static testMode = 'test';
    private static instance: Logging;
    private static errorLogHeaderStyle = 'font-weight: bold; color: #E81123;';
    private static warnLogHeaderStyle = 'font-weight: bold; color: #FF8C00;';
    private static successLogHeaderStyle = 'font-weight: bold; color: ##288928;';
    private static infoLogHeaderStyle = 'font-weight: bold; color: #0078D7;';
    private static verboseLogHeaderStyle = 'color: #999';

    private logSubjectServer: RpcLogSubjectServer;
    private telemetrySubjectServer: RpcTelemetrySubjectServer;
    private gateway: GatewayConnection;
    private http: Http;
    private rpc: Rpc;
    private logSet: LoggerSet<RpcLogRecord>;
    private telemetrySet: LoggerSet<RpcTelemetryRecord>;

    private thresholdOfLogLevel: LogLevel = LogLevel.Informational;
    private verboseTelemetry = false;

    /**
     * Log a logging event.
     *
     * @param record the log record to send the gateway.
     * @return Promise<any> settle to resolve if buffered.
     */
    public static log(record: LogRecord): Promise<any> {
        return Logging.current.logInternal(record);
    }

    /**
     * Log a critical logging event.
     *
     * @param source The record originator of event within a module.
     * @param message The message of event.
     * @param params The parameters object which will be serialized.
     * @return Promise<any> settle to resolve if buffered.
     */
    public static logCritical(source: string, message: string, params?: any): Promise<any> {
        return Logging.current.logInternal({ level: LogLevel.Critical, source, message, params: params });
    }

    /**
     * Log an error logging event.
     *
     * @param source The record originator of event within a module.
     * @param message The message of event.
     * @param params The parameters object which will be serialized.
     * @return Promise<any> settle to resolve if buffered.
     */
    public static logError(source: string, message: string, params?: any): Promise<any> {
        return Logging.current.logInternal({ level: LogLevel.Error, source, message, params: params });
    }

    /**
     * Log a warning logging event.
     *
     * @param source The record originator of event within a module.
     * @param message The message of event.
     * @param params The parameters object which will be serialized.
     * @return Promise<any> settle to resolve if buffered.
     */
    public static logWarning(source: string, message: string, params?: any): Promise<any> {
        return Logging.current.logInternal({ level: LogLevel.Warning, source, message, params: params });
    }

    /**
     * Log a success logging event.
     *
     * @param source The record originator of event within a module.
     * @param message The message of event.
     * @param params The parameters object which will be serialized.
     * @return Promise<any> settle to resolve if buffered.
     */
    public static logSuccess(source: string, message: string, params?: any): Promise<any> {
        return Logging.current.logInternal({ level: LogLevel.Success, source, message, params: params });
    }

    /**
     * Log a informational logging event.
     *
     * @param source The record originator of event within a module.
     * @param message The message of event.
     * @param params The parameters object which will be serialized.
     * @return Promise<any> settle to resolve if buffered.
     */
    public static logInformational(source: string, message: string, params?: any): Promise<any> {
        return Logging.current.logInternal({ level: LogLevel.Informational, source, message, params: params });
    }

    /**
     * Log a verbose logging event.
     *
     * @param source The record originator of event within a module.
     * @param message The message of event.
     * @param params The parameters object which will be serialized.
     * @return Promise<any> settle to resolve if buffered.
     */
    public static logVerbose(source: string, message: string, params?: any): Promise<any> {
        return Logging.current.logInternal({ level: LogLevel.Verbose, source, message, params: params });
    }

    /**
     * Log a debug logging event.
     *
     * @param source The record originator of event within a module.
     * @param message The message of event.
     * @param params The parameters object which will be serialized.
     * @return Promise<any> settle to resolve if buffered.
     */
    public static logDebug(source: string, message: string, params?: any): Promise<any> {
        return Logging.current.logInternal({ level: LogLevel.Debug, source, message, params: params });
    }

    /**
     * Trace a telemetry event.
     *
     * @param record the telemetry record to send the gateway.
     * @return Promise<any> settle to resolve if buffered.
     */
    public static trace(record: TelemetryRecord): Promise<any> {
        return Logging.current.telemetryInternal(record);
    }

    /**
     * Trace a user action telemetry event.
     *
     * @param data additional data.
     * @return Promise<any> settle to resolve if buffered.
     */
    public static traceUserAction(data: any): Promise<any> {
        return Logging.trace({
            view: LoggingConstants.views.smeUIControl,
            instance: LoggingConstants.empty,
            action: LoggingConstants.actions.click,
            data: data
        });
    }

    /**
     * Trace a telemetry event for a button click.
     *
     * @param controlName Describes which control was used.
     * ex) For a button that says 'Set up' the control name can be 'Set up' to distinguish that from a cancel button.
     * @param controlId Unique identifier when a control appears more than once. Specific to how implementation handles uniqueness.
     * @param nodeName the name of the node currently connected to (this.appContextService.activeConnection.nodeName)
     * @param nodeType the type of node currently connected to (this.appContextService.connectionManager.activeConnection.type)
     * @return Promise<any> settle to resolve if buffered.
     */
    public static traceButton(controlName: string, controlId?: string, nodeName?: string, nodeType?: string): Promise<any> {
        return Logging.traceButtonClick(TelemetryControlType.Button, controlName, controlId, nodeName, nodeType);
    }

    /**
     * Trace a telemetry event for a action pane button click.
     *
     * @param controlName Describes which control was used.
     * ex) For a button that says 'Set up' the control name can be 'Set up' to distinguish that from a cancel button.
     * @param controlId Unique identifier when a control appears more than once. Specific to how implementation handles uniqueness.
     * @param nodeName the name of the node currently connected to (this.appContextService.activeConnection.nodeName)
     * @param nodeType the type of node currently connected to (this.appContextService.connectionManager.activeConnection.type)
     * @return Promise<any> settle to resolve if buffered.
     */
    public static traceActionPaneButton(controlName: string, controlId?: string, nodeName?: string, nodeType?: string): Promise<any> {
        return Logging.traceButtonClick(TelemetryControlType.ActionPaneButton, controlName, controlId, nodeName, nodeType);
    }

    /**
  * Trace a telemetry event for a action bar button click.
  *
  * @param controlName Describes which control was used.
  * ex) For a button that says 'Set up' the control name can be 'Set up' to distinguish that from a cancel button.
  * @param controlId Unique identifier when a control appears more than once. Specific to how implementation handles uniqueness.
  * @param nodeName the name of the node currently connected to (this.appContextService.activeConnection.nodeName)
  * @param nodeType the type of node currently connected to (this.appContextService.connectionManager.activeConnection.type)
  * @return Promise<any> settle to resolve if buffered.
  */
    public static traceActionBarButton(controlName: string, controlId?: string, nodeName?: string, nodeType?: string): Promise<any> {
        return Logging.traceButtonClick(TelemetryControlType.ActionBarButton, controlName, controlId, nodeName, nodeType);
    }

    /**
     * Trace a telemetry event on a sme control.
     *
     * @param controlName Describes which control was used.
     * ex) For a button that says 'Set up' the control name can be 'Set up' to distinguish that from a cancel button.
     * @param controlId Unique identifier when a control appears more than once. Specific to how implementation handles uniqueness.
     * @param additionalData Additional key value pairs that can be sent to telemetry.
     * @param nodeName the name of the node currently connected to (this.appContextService.activeConnection.nodeName)
     * @param nodeType the type of node currently connected to (this.appContextService.connectionManager.activeConnection.type)
     * @return Promise<any> settle to resolve if buffered.
     */
    public static traceControl(
        controlType: TelemetryControlType,
        controlName: string,
        controlId?: string,
        additionalData?: any,
        nodeName?: string,
        nodeType?: string): Promise<any> {
        let data = {};

        if (additionalData) {
            data = additionalData;
        }
        data[controlType] = controlType;
        data[controlName] = controlName;
        data[controlId] = controlId;
        const nodeId = Logging.getFormattedNodeId(nodeName, nodeType);
        if (nodeId) {
            data[LoggingConstants.dataFields.nodeId] = nodeId;
        }

        return Logging.traceUserAction(data);
    }

    /**
     * Trace a telemetry event on click of an external link.
     *
     * @param controlName Describes which control was used.
     * ex) For a button that says 'Set up' the control name can be 'Set up' to distinguish that from a cancel button.
     * @param url External link that was clicked.
     * @param controlId Unique identifier when a control appears more than once. Specific to how implementation handles uniqueness.
     * @param nodeName the name of the node currently connected to (this.appContextService.activeConnection.nodeName)
     * @param nodeType the type of node currently connected to (this.appContextService.connectionManager.activeConnection.type)
     * @return Promise<any> settle to resolve if buffered.
     */
    public static traceExternalLink(
        controlName: string,
        url: string,
        controlId?: string,
        nodeName?: string,
        nodeType?: string): Promise<any> {
        const data = {
            controlType: TelemetryControlType.ExternalLink,
            controlName: controlName,
            controlId: controlId,
            url: url
        };

        const nodeId = Logging.getFormattedNodeId(nodeName, nodeType);
        if (nodeId) {
            data[LoggingConstants.dataFields.nodeId] = nodeId;
        }

        return Logging.traceUserAction(data);
    }

    /**
     * Trace a telemetry event on click of an internal link.
     *
     * @param controlName Describes which control was used.
     * ex) For a button that says 'Set up' the control name can be 'Set up' to distinguish that from a cancel button.
     * @param route rpc route.
     * @param controlId Unique identifier when a control appears more than once. Specific to how implementation handles uniqueness.
     * @param nodeName the name of the node currently connected to (this.appContextService.activeConnection.nodeName)
     * @param nodeType the type of node currently connected to (this.appContextService.connectionManager.activeConnection.type)
     * @return Promise<any> settle to resolve if buffered.
     */
    public static traceInternalLink(
        controlName: string,
        route: string,
        controlId?: string,
        nodeName?: string,
        nodeType?: string): Promise<any> {
        const data =  {
            controlType: TelemetryControlType.InternalLink,
            controlName: controlName,
            controlId: controlId,
            route: route
        };

        const nodeId = Logging.getFormattedNodeId(nodeName, nodeType);
        if (nodeId) {
            data[LoggingConstants.dataFields.nodeId] = nodeId;
        }

        return Logging.traceUserAction(data);
    }

    /**
     * Trace a telemetry event when an async task returns.
     *
     * @param eventLocation UI location of the event being recorded.
     * ex) createVmForm, RegisterAadDialog, stopServiceConfirmationDialog...
     * @param eventLabel Description of what event is being logged.
     * ex) 'Registered with Azure', 'Added a connection', 'Failed Azure backup'
     * @param result Result of event.
     * @param additionalData Additional key value pairs that can be sent to telemetry.
     * @param nodeName the name of the node currently connected to (this.appContextService.activeConnection.nodeName)
     * @param nodeType the type of node currently connected to (this.appContextService.connectionManager.activeConnection.type)
     * @return Promise<any> settle to resolve if buffered.
     */
    public static traceAsyncResult(
        eventLocation: string,
        eventLabel: string,
        result: 'Success' | 'Error' | 'Failure',
        additionalData?: any,
        nodeName?: string,
        nodeType?: string): Promise<any> {
        let data = {};
        if (additionalData) {
                data = additionalData;
        }

        data[LoggingConstants.dataFields.eventLocation] = eventLocation;
        data[LoggingConstants.dataFields.eventLabel] = eventLabel;
        data[LoggingConstants.dataFields.result] = result;

        const nodeId = Logging.getFormattedNodeId(nodeName, nodeType);
        if (nodeId) {
            data[LoggingConstants.dataFields.nodeId] = nodeId;
        }

        return Logging.traceUserAction(data);
    }

    /**
     * Log a raw object into the console at debug level of mode.
     */
    public static debug(object: any): void {
        if (Logging.current.consoleLogLevel >= LogLevel.Debug) {
            console.log(object);
        }
    }

    /**
     * Configure logging mode.
     *
     * @param thresholdOfLogLevel the log level for gateway.
     * @param verboseTelemetry if true, optional telemerty will be collected.
     */
    public static configureLog(thresholdOfLogLevel: LogLevel, verboseTelemetry: boolean): void {
        Logging.current.thresholdOfLogLevel = thresholdOfLogLevel;
        Logging.current.verboseTelemetry = verboseTelemetry;
    }

    /**
     * Wrapper method for tracing a telemetry event for a button click.
     *
     * @param controlType Type of button that is being recorded
     * @param controlName Describes which control was used.
     * ex) For a button that says 'Set up' the control name can be 'Set up' to distinguish that from a cancel button.
     * @param controlId Unique identifier when a control appears more than once. Specific to how implementation handles uniqueness.
     * @param nodeName the name of the node currently connected to (this.appContextService.activeConnection.nodeName)
     * @param nodeType the type of node currently connected to (this.appContextService.connectionManager.activeConnection.type)
     * @return Promise<any> settle to resolve if buffered.
     */
    private static traceButtonClick(
        controlType: TelemetryControlType.ActionBarButton | TelemetryControlType.ActionPaneButton | TelemetryControlType.Button,
        controlName: string,
        controlId?: string,
        nodeName?: string,
        nodeType?: string): Promise<any> {
        const data = {
            controlType: controlType,
            controlName: controlName,
            controlId: controlId
        };

        const nodeId = Logging.getFormattedNodeId(nodeName, nodeType);
        if (nodeId) {
            data[LoggingConstants.dataFields.nodeId] = nodeId;
        }

        return Logging.traceUserAction(data);
    }

    /**
     * Format node information for telemetry
     *
     * @param nodeName the name of the node currently connected to (this.appContextService.activeConnection.nodeName)
     * @param nodeType the type of node currently connected to (this.appContextService.connectionManager.activeConnection.type)
     * @return string
     */
    private static getFormattedNodeId(nodeName?: string, nodeType?: string): string {
        if (nodeType && nodeName) {
            return nodeType + '!' + nodeName;
        } else {
            return null;
        }
    }

    /**
     * Gets the level of current logging.
     */
    public get consoleLogLevel(): LogLevel {
        const global: MsftSme.SMEWindow = <any>window;
        return <LogLevel>(global.MsftSme && global.MsftSme.Init && global.MsftSme.Init.logLevel) || LogLevel.Warning;
    }

    /**
     * Gets the session Id of shell.
     */
    private get sessionId(): string {
        const global: MsftSme.SMEWindow = <any>window;
        return global.MsftSme && global.MsftSme.Init && global.MsftSme.Init.sessionId || Logging.testMode;
    }

    /**
     * Gets the name of current shell or module.
     */
    private get nameOfModule(): string {
        const global: MsftSme.SMEWindow = <any>window;
        return global.MsftSme && global.MsftSme.Init && global.MsftSme.Init.moduleName || Logging.testMode;
    }

    /**
     * Initializes a new instance of the Logging class.
     */
    constructor() {
        this.http = new Http();
        this.logSet = {
            maxWaitTimeMs: Logging.logMaxWaitTimeMs,
            maxRecordLength: Logging.logMaxRecordLength,
            api: '/log'
        };
        this.telemetrySet = {
            maxWaitTimeMs: Logging.telemetryMaxWaitTimeMs,
            maxRecordLength: Logging.telemetryMaxRecordLength,
            api: '/telemetry'
        };
    }

    /**
     * Gets the current logging instance.
     */
    public static get current(): Logging {
        if (Logging.instance) {
            return Logging.instance;
        }

        Logging.instance = new Logging();
        return Logging.instance;
    }

    /**
     * Flush the regular log set, immediately submitting the logs to the gateway
     */
    public static flushLogs() {
        Logging.current.flush(Logging.current.logSet);
    }

    /**
     * Flush the telemetry log set, immediately submitting the logs to the gateway
     */
    public static flushTelemetry() {
        Logging.current.flush(Logging.current.telemetrySet);
    }

    /**
     * Register Rpc object to logging instance.
     *
     * @param rpc the rpc instance.
     */
    public registerRpc(rpc: Rpc, gateway: GatewayConnection): void {
        this.rpc = rpc;
        this.gateway = gateway;

        // start subscribing once after the rpc is ready on the shell.
        this.rpc.stateChanged
            .pipe(
                filter(active => active),
                take(1))
            .subscribe(() => {
                if (this.rpc.isShell) {
                    this.logSubjectServer = new RpcLogSubjectServer(this.rpc);
                    this.logSet.rpcSubscription = this.logSubjectServer.subject
                        .subscribe(data => {
                            this.logGateway<RpcLogRecord>(this.logSet, data.data);
                            data.deferred.resolve();
                        });
                    this.telemetrySubjectServer = new RpcTelemetrySubjectServer(this.rpc);
                    this.telemetrySet.rpcSubscription = this.telemetrySubjectServer.subject
                        .subscribe(data => {
                            this.logGateway<RpcTelemetryRecord>(this.telemetrySet, data.data);
                            data.deferred.resolve();
                        });
                }
            });
    }

    /**
     * Dispose the set of rpc forwarding pipes.
     */
    public dispose(): void {
        this.disposeSet<RpcLogRecord>(this.logSet);
        this.disposeSet<RpcTelemetryRecord>(this.telemetrySet);
    }

    /**
     * Log a record.
     *
     * @param record the log record.
     * @return Promise<any> the promise object.
     */
    private logInternal(record: LogRecord): Promise<any> {
        const now = new Date();
        if (record.level <= this.consoleLogLevel) {
            let headerStyle = Logging.verboseLogHeaderStyle;
            if (record.level <= LogLevel.Error) {
                headerStyle = Logging.errorLogHeaderStyle;
            } else if (record.level <= LogLevel.Warning) {
                headerStyle = Logging.warnLogHeaderStyle;
            } else if (record.level <= LogLevel.Success) {
                headerStyle = Logging.successLogHeaderStyle;
            } else if (record.level <= LogLevel.Informational) {
                headerStyle = Logging.infoLogHeaderStyle;
            }

            const logSeparator = '--';
            const customHeader = record.consoleGroupHeader ? ` ${logSeparator} ${record.consoleGroupHeader}` : '';
            if (MsftSme.isEdge() || MsftSme.isInternetExplorer()) {
                // Microsoft Edge and IE don't support styles in group headers. so remove if running on Microsoft Edge
                // Since there is no color support, also log the severity
                console.groupCollapsed(
                    `${LogLevel[record.level]} ${logSeparator} ${this.nameOfModule} ${logSeparator} ${record.source}${customHeader}`
                );
            } else {
                console.groupCollapsed(`%c${this.nameOfModule} ${logSeparator} ${record.source}${customHeader}`, headerStyle);
            }

            console.log(`logLevel: ${LogLevel[record.level]}`);
            console.log(`timestamp: ${now.toISOString()}`);
            if (record.message) {
                if (MsftSme.isObject(record.message)) {
                    console.log('message:');
                    console.log({ ...record.message });
                } else {
                    console.log(`message: ${record.message}`);
                }
            }

            if (record.params) {
                console.group('params');
                console.log({ ...record.params });
                console.groupEnd();
            }

            if (record.localParams) {
                console.group('params');
                console.log({ ...record.localParams });
                console.groupEnd();
                delete record['localParams'];
            }

            if (record.stack) {
                console.group('stack');
                console.log(record.stack);
                console.groupEnd();
            }

            console.groupEnd();
        }

        if (record.level <= this.thresholdOfLogLevel && this.sessionId !== Logging.testMode) {
            if (MsftSme.isObject(record.message)) {
                // ensure message is a string.
                record.message = JSON.stringify(record.message);
            }

            const rpcRecord: RpcLogRecord = {
                ...record,
                ...{ sessionId: this.sessionId, timestamp: now.getTime(), sourceName: this.nameOfModule }
            };
            if (this.rpc && this.rpc.stateActive && !this.rpc.isShell) {
                return RpcLogClient.log(this.rpc, rpcRecord);
            }

            this.logGateway<RpcLogRecord>(this.logSet, rpcRecord);
        }

        return Promise.resolve();
    }

    /**
     * Log a telemerty record.
     *
     * @param record the telemetry record.
     * @return Promise<any> the promise object.
     */
    private telemetryInternal(record: TelemetryRecord): Promise<any> {
        if (((!this.verboseTelemetry && !record.optional) || this.verboseTelemetry) && this.sessionId !== Logging.testMode) {
            const rpcRecord = { ...record, ...{ sessionId: this.sessionId, timestamp: Date.now(), sourceName: this.nameOfModule } };
            if (this.rpc && this.rpc.stateActive && !this.rpc.isShell) {
                return RpcTelemetryClient.telemetry(this.rpc, rpcRecord);
            }

            this.logGateway<RpcTelemetryRecord>(this.telemetrySet, rpcRecord);
        }

        return Promise.resolve();
    }

    /**
     * Dispose the set.
     *
     * @param set the logger set.
     */
    private disposeSet<T>(set: LoggerSet<T>): void {
        if (set.rpcSubscription) {
            set.rpcSubscription.unsubscribe();
            set.rpcSubscription = null;
        }
    }

    /**
     * Log to the gateway.
     *
     * @param set the logger set.
     * @param data the record data.
     */
    private logGateway<T>(set: LoggerSet<T>, data: T): void {
        if (set.timer == null) {
            set.buffer = [];
            set.timer = setTimeout(() => this.submitRecords<T>(set), set.maxWaitTimeMs);
        }

        set.buffer.push(data);
        if (set.buffer.length >= set.maxRecordLength) {
            clearTimeout(set.timer);
            this.submitRecords(set);
            set.timer = setTimeout(() => this.submitRecords<T>(set), set.maxWaitTimeMs);
        }
    }

    /**
     * Flush a log set, immediately submitting the logs to the gateway
     */
    private flush<T>(set: LoggerSet<T>) {
        if (set && set.buffer && set.buffer.length > 0) {
            this.submitRecords(set);
        }
    }

    /**
     * Submit records to the gateway.
     */
    private submitRecords<T>(set: LoggerSet<T>): void {
        // Disabled gateway can't log anything in gateway.
        if (set.buffer.length > 0 && this.gateway && !this.gateway.disabled) {
            this.gateway.post(set.api, set.buffer, <GatewayRequest>{ maxRetryCount: 0 }).subscribe({
                error: error => {
                    const message = MsftSme.getStrings<Strings>().MsftSmeShell.Core.Error.LoggingUnableSubmit.message;
                    console.error(message.format(Net.getErrorMessage(error)));
                }
            });
        }

        set.timer = null;
        set.buffer = [];
    }
}
